import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, ExternalLink, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EHOUSE_LOGIN_URL = 'https://ehouse.ai/account/login';
const EHOUSE_API_DOCS = 'https://public-api.staging.ehouse.ai';

/**
 * Admin panel for the eHouse (ihouse.ai) shipping integration.
 * Stores API Key + Secret + base URL in MongoDB so the dashboard can push
 * orders to eHouse via the "שלח עם E-HOUSE" button on each order card.
 */
const EhouseConfig = () => {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ehouse-config`);
      setCfg(data);
      setBaseUrl(data.base_url || '');
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!apiKey && !apiSecret && !baseUrl) {
      toast.error('יש להזין לפחות שדה אחד');
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (apiKey) body.api_key = apiKey;
      if (apiSecret) body.api_secret = apiSecret;
      if (baseUrl && baseUrl !== cfg?.base_url) body.base_url = baseUrl;
      await axios.post(`${API}/admin/ehouse-config`, body);
      toast.success('הגדרות E-HOUSE נשמרו');
      setApiKey('');
      setApiSecret('');
      setTimeout(load, 600);
    } catch (e) {
      toast.error('שגיאה: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  if (!cfg) return null;

  const isConfigured = cfg.configured;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="ehouse-config-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors"
        data-testid="ehouse-config-toggle"
      >
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4 text-emerald-600" />
          <span className="font-bold text-sm text-stone-800">E-HOUSE שילוח</span>
          {isConfigured ? (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full" data-testid="ehouse-status-ok">
              <CheckCircle2 className="w-3 h-3" />
              מחובר
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full" data-testid="ehouse-status-missing">
              <AlertCircle className="w-3 h-3" />
              לא מוגדר
            </span>
          )}
        </div>
        <span className="text-[10px] text-stone-400">{expanded ? '▲ סגור' : '▼ פתח'}</span>
      </button>

      {expanded && (
        <div className="p-3 border-t border-stone-200 space-y-3">
          {/* Help row */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-[11px] text-emerald-900 space-y-1">
            <p className="font-bold">איך משיגים API Key + Secret?</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>
                התחבר ל-
                <a href={EHOUSE_LOGIN_URL} target="_blank" rel="noreferrer" className="underline font-bold inline-flex items-center gap-0.5">
                  ehouse.ai
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              </li>
              <li>בהגדרות / Integrations / API Keys — צור מפתח חדש</li>
              <li>העתק את <b>API Key</b> וגם את <b>Secret</b> (Secret מוצג פעם אחת בלבד!)</li>
              <li>הדבק כאן ולחץ שמור</li>
            </ol>
          </div>

          {/* Current status row */}
          {isConfigured && (
            <div className="flex items-center justify-between bg-stone-50 rounded-lg p-2 text-[11px]" data-testid="ehouse-current-config">
              <span className="text-stone-500">מפתח נוכחי:</span>
              <code className="text-stone-700 font-mono">{cfg.api_key_preview || '—'}</code>
            </div>
          )}

          {/* Form */}
          <div className="space-y-2">
            <div>
              <label className="text-[11px] font-bold text-stone-600 block mb-1">API Key</label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="הדבק את ה-API Key כאן"
                className="w-full px-3 py-2 text-xs border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="ehouse-input-key"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold text-stone-600 block mb-1">API Secret</label>
              <input
                type="password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="הדבק את ה-Secret כאן"
                className="w-full px-3 py-2 text-xs border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="ehouse-input-secret"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold text-stone-600 block mb-1">
                Base URL
                <span className="font-normal text-stone-400 mr-1">(אם משתמשים ב-staging השאר ברירת מחדל)</span>
              </label>
              <input
                type="text"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://public-api.staging.ehouse.ai"
                className="w-full px-3 py-2 text-xs border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="ehouse-input-url"
              />
            </div>

            <button
              onClick={save}
              disabled={saving}
              className="w-full px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50"
              data-testid="ehouse-save-btn"
            >
              {saving ? 'שומר…' : 'שמור הגדרות E-HOUSE'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EhouseConfig;
